import { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';

import { EditorableSchema } from 'dashboard/components-lib/FieldSchemaEditor/constants';
import { TableCell, TableRow } from 'design-system/components/Table';
import { isFocused } from 'design-system/lib/pseudoClass';
import { TransitionDuration } from 'design-system/variables/transitions';

const InserterTableCell = styled(TableCell)<{ nestSpacing: number }>`
  padding: 0;
  ${({ nestSpacing }) => (nestSpacing ? `padding-left: ${nestSpacing}px` : '')};
  height: 0;
  position: relative;
  border: 0;
  z-index: 1;
`;

const StyledTableRow = styled(TableRow)`
  &${isFocused} {
    box-shadow: none;
  }
`;

const HoverArea = styled.div<{ nestSpacing: number }>`
  position: absolute;
  height: 20px;
  top: -10px;
  width: ${({ nestSpacing }) => (nestSpacing ? `calc(100% - ${nestSpacing}px)` : '100%')};
`;

const AddFieldLine = styled.div<{ visible: boolean; nestSpacing: number }>`
  position: absolute;
  height: ${({ visible }) => (visible ? '2px' : '0')};
  background-color: ${({ theme }) => theme.color.primary.brand};
  width: ${({ nestSpacing }) => (nestSpacing ? `calc(100% - ${nestSpacing}px)` : '100%')};
  transition-duration: ${TransitionDuration.Fast};
`;

const AddFieldMarker = styled.span`
  position: absolute;
  top: -7px;
  height: 15px;
  width: 15px;
  border-radius: 10px;
  background-color: ${({ theme }) => theme.color.primary.brand};
  cursor: pointer;
  color: ${({ theme }) => theme.color.text.inverse};
  &:before {
    content: '+';
    position: relative;
    top: 1px;
    left: 4px;
  }
`;

const HALF_BUTTON_WIDTH = 8;

interface InserterProps {
  prefix: string;
  colSpan: number;
  nestSpacing?: number;
  label?: string;
  onAddRow: (schema: EditorableSchema) => void;
}

export const Inserter: React.FC<InserterProps> = ({
  prefix,
  colSpan,
  nestSpacing = 0,
  label,
  onAddRow,
}) => {
  const cellRef = useRef<HTMLTableCellElement>();
  const [tokenPositionX, setTokenPositionX] = useState(0);
  const [visible, setVisible] = useState(false);

  const toggleInserter = (event: MouseEvent) => {
    if (!cellRef.current) {
      return;
    }
    const { x, y, height, width } = cellRef.current.getBoundingClientRect();
    if (
      Math.abs(y + height - event.clientY) < 10 &&
      event.clientX > x + nestSpacing + HALF_BUTTON_WIDTH &&
      event.clientX < x + width - HALF_BUTTON_WIDTH
    ) {
      // Do this during onmousemove on window because calculating the position during onmouseover
      // on the element itself has too much lagging and flickering
      setTokenPositionX(event.clientX - x);
    }
  };
  useEffect(() => {
    window.addEventListener('mousemove', toggleInserter);
    return () => {
      window.removeEventListener('mousemove', toggleInserter);
    };
  });
  return (
    <StyledTableRow
      onMouseLeave={() => setVisible(false)}
      onFocus={() => setVisible(true)}
      onBlur={() => setVisible(false)}
      onKeyDown={(evt) => {
        if (evt.key === ' ' || evt.key === 'Enter') {
          evt.preventDefault();
          evt.stopPropagation();
          onAddRow({
            /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
            type: null,
            config: {},
            key: '',
            label: '',
          });
        }
      }}
      tabIndex={0}
    >
      {/* @ts-expect-error [strict-migration] The following line is not strict; please fix! */}
      <InserterTableCell colSpan={colSpan} ref={cellRef} nestSpacing={nestSpacing}>
        <HoverArea
          nestSpacing={nestSpacing}
          onMouseOver={() => {
            setVisible(true);
          }}
        />
        <AddFieldLine visible={visible} nestSpacing={nestSpacing} />
        <AddFieldMarker
          id={`${prefix}-inserter`}
          role='button'
          aria-label={label || `Insert a field after ${prefix}`}
          onClick={() => {
            onAddRow({
              'default-value': null,
              /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
              type: null,
              config: {},
              key: '',
              label: '',
            });
          }}
          style={{ left: `${tokenPositionX - 7}px`, display: visible ? 'inherit' : 'none' }}
        />
      </InserterTableCell>
    </StyledTableRow>
  );
};
